import camelCase from 'lodash.camelcase';
import * as React from 'react';

import routes from 'defs/routes';

import Bevel from 'components/helper/Bevel';
import i18n from 'helper/i18n';

import Award, { AwardProps } from 'components/atoms/Award';
import Link from 'components/atoms/Link';
import ProgressRadial from 'components/atoms/ProgressRadial';

const calculatePercentage = (done: number, total: number): number => {
	if (total === 0) {
		return 0;
	}

	return (done / total) * 100;
};

export type BadgeType = AwardProps & {
	id: string;
	level: string;
	eventType: string;
};

export type ProgramSuccessProps = {
	badges: Array<BadgeType>;
	numberOfWorkouts: number;
	numberOfWorkoutsDone: number;
	numberOfRecipes: number;
	numberOfRecipesDone: number;
	numberOfTips: number;
	numberOfTipsDone: number;
	numberOfTodos: number;
	numberOfTodosDone: number;
	onCloseNotification: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

/**
 * Transforms a flat list of badges into a list of tuples, E.g. [1, 2, 3] => [[1, 2], [3, null]]
 * @param {any[]} badges
 * @returns {any[]}
 */
const getBadgeMatrix = (badges: BadgeType[]): any[] => {
	const matrix = badges.reduce(
		(acc, cur: BadgeType) => {
			if (acc[acc.length - 1].length < 2) {
				acc[acc.length - 1].push(cur);
			} else {
				acc.push([cur]);
			}

			return acc;
		},
		[[]] as any
	);

	if (matrix[matrix.length - 1].length < 2) {
		matrix[matrix.length - 1].push(null);
	}

	return matrix;
};

const renderAwardBadges = (props: ProgramSuccessProps): Array<any> | null => {
	const badges = props.badges || [];
	const badgeMatrix = getBadgeMatrix(badges);

	if (badges.length > 0) {
		return badgeMatrix.map(
			(row: BadgeType[], i1: number): React.ReactNode => {
				const output = [<hr key={`badges-line-${i1}`} className="m-tiles__line" />];

				const renderedBadges = row.map(
					(badge: BadgeType, i2: number): JSX.Element => {
						const badgeId = `${i1}-${i2}`;
						const key = badge ? `${badge.id}-${badge.level}` : `empty$badge-${badgeId}`;
						const modifier = badge ? '' : ' m-tiles__cell--empty';
						const label = badge
							? i18n(
									`react.impulsCoach.events.${camelCase(badge.eventType)}.${camelCase(
										badge.code
									)}.${camelCase(badge.eventLevel)}.title`
							  )
							: null;

						return (
							<div key={`${key}-${badgeId}`} className={`m-tiles__cell${modifier}`}>
								{badge && [
									<div key={`badge-${badgeId}-box`} className="m-tiles__box">
										<Award code={badge.code} eventLevel={badge.eventLevel} />
									</div>,
									<div key={`badge-${badgeId}-label`} className="m-tiles__box-label">
										{label}
									</div>,
								]}
							</div>
						);
					}
				);

				output.push(
					<div key={`badges-row-${i1}`} className="m-tiles__row">
						{renderedBadges}
					</div>
				);

				return output;
			}
		);
	}
	return null;
};

const ProgramSuccess = (props: ProgramSuccessProps) => {
	return (
		<div className="m-program-success">
			<div className="container">
				<div className="row">
					<div className="col-xs-12">
						<div className="m-program-success__content">
							<h2 className="m-program-success__title">
								{i18n('react.impulsCoach.molecules.ProgramSuccess.title')}
							</h2>
							<div className="m-tiles">
								<div className="m-tiles__row">
									<div className="m-tiles__cell">
										<div className="m-tiles__box">
											<ProgressRadial
												reach={props.numberOfWorkoutsDone}
												total={props.numberOfWorkouts}
												icon="workout"
												percent={calculatePercentage(
													props.numberOfWorkoutsDone,
													props.numberOfWorkouts
												)}
												meterColor="017580"
												valueColor="48BFA2"
											/>
										</div>
										<span className="m-tiles__box-label">
											{i18n('react.impulsCoach.molecules.ProgramSuccess.workouts.label')}
										</span>
									</div>

									<div className="m-tiles__cell">
										<div className="m-tiles__box">
											<ProgressRadial
												reach={props.numberOfRecipesDone}
												total={props.numberOfRecipes}
												icon="recipe"
												percent={calculatePercentage(
													props.numberOfRecipesDone,
													props.numberOfRecipes
												)}
												meterColor="017580"
												valueColor="48BFA2"
											/>
										</div>
										<span className="m-tiles__box-label">
											{i18n('react.impulsCoach.molecules.ProgramSuccess.recipes.label')}
										</span>
									</div>
								</div>
								<hr className="m-tiles__line" />
								<div className="m-tiles__row">
									<div className="m-tiles__cell">
										<div className="m-tiles__box">
											<ProgressRadial
												reach={props.numberOfTipsDone}
												total={props.numberOfTips}
												icon="tip"
												percent={calculatePercentage(
													props.numberOfTipsDone,
													props.numberOfTips
												)}
												meterColor="017580"
												valueColor="48BFA2"
											/>
										</div>
										<span className="m-tiles__box-label">
											{i18n('react.impulsCoach.molecules.ProgramSuccess.tips.label')}
										</span>
									</div>

									<div className="m-tiles__cell">
										<div className="m-tiles__box">
											<ProgressRadial
												reach={props.numberOfTodosDone}
												total={props.numberOfTodos}
												icon="task"
												percent={calculatePercentage(
													props.numberOfTodosDone,
													props.numberOfTodos
												)}
												meterColor="017580"
												valueColor="48BFA2"
											/>
										</div>
										<span className="m-tiles__box-label">
											{i18n('react.impulsCoach.molecules.ProgramSuccess.todos.label')}
										</span>
									</div>
								</div>
								{renderAwardBadges(props)}
							</div>
						</div>
					</div>
				</div>
			</div>

			<Bevel modifier="bottom-left" color="ffffff" />

			<div className="m-program-success__bottom">
				<div className="container">
					<div className="row">
						<div className="col-xs-12 col-sm-12 col-md-6 col-md-offset-6">
							<div className="m-program-success__link">
								<Link
									modifier="button-green"
									title={i18n('react.impulsCoach.molecules.ProgramSuccess.startNew')}
									href={routes.program.recommended.href}
									onClick={props.onCloseNotification}
									iconRight="link-arrow"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ProgramSuccess;
